import { useState, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Users, MessageCircle, Send, Instagram, Facebook, Youtube, Music2, UserCircle, LogIn, ChevronRight } from "lucide-react";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import BrandLogo from "@/components/icons/BrandLogo";
import SectionCta from "@/components/SectionCta";

// Meniul principal cerut în documentele de brand (De la George Txt.docx):
// Acasă · Despre · Ședințe · Tehnici · Testimoniale · Contact.
// Cursuri, Biblioteca, Pădurea, Comunitate și „Ajutor ACUM" (sos) au fost
// scoase din navigare (rutele rămân funcționale, accesibile direct pe URL —
// vezi backend/deactivate_non_document_content.py pentru revert).
const NAV = [
  { to: "/", label: "Acasă", end: true },
  { to: "/despre-mine", label: "Despre" },
  { to: "/sedinte", label: "Ședințe" },
  { to: "/tehnici", label: "Tehnici" },
  { to: "/recenzii", label: "Testimoniale" },
  { to: "/contact", label: "Contact" },
];

const SOCIAL = [
  { key: "whatsapp_community", icon: MessageCircle, label: "WhatsApp" },
  { key: "telegram", icon: Send, label: "Telegram" },
  { key: "tiktok", icon: Music2, label: "TikTok" },
  { key: "youtube", icon: Youtube, label: "YouTube" },
  { key: "instagram", icon: Instagram, label: "Instagram" },
  { key: "facebook", icon: Facebook, label: "Facebook" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [menuPages, setMenuPages] = useState([]);
  const { site, sectionContent } = useSite();
  const { user } = useAuth();
  // Cele 3 butoane „Programează o sesiune" (desktop/tabletă/mobil) citesc toate
  // aceeași secțiune nouă `header_cta` (cta-spec.md secțiunea F.1) — înainte
  // erau hardcodate, în afara oricărei secțiuni CMS, deci needitabile din
  // Live Editor.
  const headerCta = sectionContent("header_cta") || {};
  const brand = site?.brand?.name || "Terapeut Georgiana";
  const tagline = site?.brand?.tagline || "Minte. Suflet. Transformare.";
  const social = site?.social || {};
  const initials = brand.split(" ").map((p) => p[0]).slice(0, 2).join("");
  const programLink = "/programare"; // motorul de ședințe 1:1
  const communityLink = "/comunitate";

  // Fetch dynamic pages flagged "show_in_menu"
  useEffect(() => {
    api.get("/public/pages")
      .then((r) => setMenuPages((r.data || []).filter((p) => p.show_in_menu)))
      .catch(() => setMenuPages([]));
  }, []);

  const navItems = [...NAV];
  // Inject menu pages BEFORE Contact (SOS/"Ajutor ACUM" rămâne mereu ultimul)
  const contactIdx = navItems.findIndex((n) => n.to === "/contact");
  const insertAt = contactIdx >= 0 ? contactIdx : navItems.length - 1;
  menuPages.forEach((p, i) => {
    navItems.splice(insertAt + i, 0, {
      to: `/p/${p.slug}`,
      label: p.menu_label || p.title,
    });
  });

  // Lock body scroll while mobile menu open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // A11y: move focus into the drawer on open, restore to opener on close,
  // close on Escape, and trap Tab within the drawer (+ close button).
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement;
    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusable = () => {
      const list = [];
      if (closeBtnRef.current) list.push(closeBtnRef.current);
      if (dialogRef.current) list.push(...dialogRef.current.querySelectorAll(FOCUSABLE));
      return list.filter((el) => el && !el.hasAttribute("disabled"));
    };
    // Move focus into the drawer (close button first).
    const initial = getFocusable();
    (initial[0] || dialogRef.current)?.focus();
    const onKeyDown = (e) => {
      if (e.key === "Escape") { e.preventDefault(); setOpen(false); return; }
      if (e.key !== "Tab") return;
      const items = getFocusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !items.includes(active)) { e.preventDefault(); last.focus(); }
      } else if (active === last || !items.includes(active)) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl border-b"
      style={{ background: "var(--ct-header-bg)", borderColor: "var(--ct-header-border)" }}
    >
      <div className="max-w-[1440px] mx-auto px-4 lg:px-12">
        <div className="flex items-center justify-between gap-4 h-[68px] lg:h-[84px]">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3 shrink-0">
            <BrandLogo size={42} />
            <span className="flex flex-col leading-tight whitespace-nowrap">
              <span className="font-serif text-lg md:text-xl" style={{ color: "var(--ct-header-text)" }}>{brand}</span>
              <span className="text-[9px] md:text-[10px] tracking-[0.24em] uppercase" style={{ color: "var(--ct-gold)" }}>{tagline}</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-6 flex-1 justify-center">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  n.sos
                    ? "text-sm whitespace-nowrap px-3 py-1 rounded-full transition-opacity hover:opacity-85"
                    : `text-sm transition-colors whitespace-nowrap ${isActive ? "text-[var(--ct-gold)] font-medium" : "text-[var(--ct-header-text-muted)] hover:text-[var(--ct-gold)]"}`
                }
                style={n.sos ? { border: "1px solid var(--ct-border-gold)", color: "var(--ct-gold-soft)", background: "rgba(212,175,55,0.10)" } : undefined}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3 shrink-0">
            <SectionCta
              content={headerCta}
              prefix="cta"
              fallbackText="Programează o sesiune"
              fallbackLink={programLink}
              className="inline-flex items-center justify-center gap-2 h-12 px-[22px] rounded-[3px] text-sm font-semibold whitespace-nowrap transition-all hover:brightness-105 bg-[var(--ct-gold)] text-[var(--ct-btn-primary-text)] border border-[var(--ct-gold)] shadow-[var(--ct-shadow-gold)]"
            />
            {user && user.role === "customer" ? (
              <Link
                to="/cont"
                data-testid="header-account-cta"
                className="inline-flex items-center justify-center gap-2 rounded-[3px] text-sm font-medium whitespace-nowrap"
                style={{ height: "48px", padding: "0 22px", background: "transparent", color: "var(--ct-header-text)", border: "1px solid var(--ct-border-gold)" }}
              >
                <UserCircle size={16} style={{ color: "var(--ct-gold)" }} /> <span>Contul meu</span>
              </Link>
            ) : user && user.role === "admin" ? (
              <Link
                to="/admin"
                data-testid="header-admin-cta"
                className="inline-flex items-center justify-center gap-2 rounded-[3px] text-sm font-medium whitespace-nowrap"
                style={{ height: "48px", padding: "0 22px", background: "transparent", color: "var(--ct-header-text)", border: "1px solid var(--ct-border-gold)" }}
              >
                <UserCircle size={16} style={{ color: "var(--ct-gold)" }} /> <span>Admin</span>
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="header-login-cta"
                className="inline-flex items-center justify-center gap-2 rounded-[3px] text-sm font-medium whitespace-nowrap"
                style={{ height: "48px", padding: "0 22px", background: "transparent", color: "var(--ct-header-text)", border: "1px solid var(--ct-border-gold)" }}
              >
                <LogIn size={16} style={{ color: "var(--ct-gold)" }} /> <span>Intră în cont</span>
              </Link>
            )}
          </div>

          {/* Tablet: only show primary CTA + hamburger for nav */}
          <div className="hidden lg:flex xl:hidden items-center gap-2 shrink-0">
            <SectionCta
              content={headerCta}
              prefix="cta"
              fallbackText="Programează o sesiune"
              fallbackLink={programLink}
              className="btn-primary !py-2 !px-3.5 text-[13px] whitespace-nowrap"
            />
            <button
              type="button"
              className="p-2 rounded-full border"
              onClick={() => setOpen(true)}
              aria-label="Deschide meniul"
              aria-expanded={open}
              aria-controls="mobile-menu"
              data-testid="tablet-menu-open"
              style={{ color: "var(--ct-header-text)", borderColor: "rgba(255,255,255,0.22)" }}
            >
              <Menu size={22} />
            </button>
          </div>

          <button
            type="button"
            className="lg:hidden p-2"
            onClick={() => setOpen(true)}
            aria-label="Deschide meniul"
            aria-expanded={open}
            aria-controls="mobile-menu"
            data-testid="mobile-menu-open"
            style={{ color: "var(--ct-header-text)" }}
          >
            <Menu size={26} />
          </button>
        </div>
      </div>

      {/* Mobile / tablet drawer — OPAQUE */}
      {open && (
        <>
          <button
            type="button"
            aria-label="Închide meniul (overlay)"
            data-testid="mobile-menu-overlay"
            onClick={() => setOpen(false)}
            className="lg:hidden xl:hidden fixed inset-0 z-40"
            style={{ background: "rgba(7,19,31,0.55)", backdropFilter: "blur(4px)" }}
          />
          <div
            ref={dialogRef}
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Meniu de navigare"
            tabIndex={-1}
            data-testid="mobile-menu"
            className="lg:hidden xl:hidden fixed top-[82px] right-4 z-50 flex flex-col overflow-hidden"
            style={{
              width: "min(82vw, 330px)",
              maxHeight: "calc(100vh - 100px)",
              background: "var(--ct-surface)",
              border: "1px solid var(--ct-border)",
              borderRadius: "6px",
              boxShadow: "0 20px 60px rgba(13,27,42,0.22)",
              animation: "slideInRight 220ms ease-out",
            }}
          >
            <div className="flex items-center justify-between px-6 pt-6 pb-3">
              <div className="flex items-center gap-2">
                <BrandLogo size={32} />
                <span className="font-serif text-base" style={{ color: "var(--ct-text)" }}>{brand}</span>
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto px-6 pb-6">
              <div className="flex flex-col">
                {navItems.map((n, idx) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 text-base font-semibold transition-colors ${idx === 0 ? "" : "border-t"} ${isActive ? "text-[var(--ct-link)]" : "text-[var(--ct-text)]"}`
                    }
                    style={{ borderColor: "var(--ct-border)", height: "56px" }}
                  >
                    <ChevronRight size={16} style={{ color: "var(--ct-gold)" }} />
                    {n.label}
                  </NavLink>
                ))}
              </div>

              <div className="mt-[18px] flex flex-col gap-[10px]">
                {/* onClick pe wrapper (nu pe SectionCta — nu expune prop pentru el) închide
                    drawer-ul la click, prin bubbling din <a>/<Link> randat înăuntru. */}
                <div onClick={() => setOpen(false)} data-testid="mobile-program-cta">
                  <SectionCta
                    content={headerCta}
                    prefix="cta"
                    fallbackText="Programează o sesiune"
                    fallbackLink={programLink}
                    className="inline-flex items-center justify-center gap-2 h-12 w-full rounded-[3px] text-sm font-semibold bg-[var(--ct-btn-primary-bg)] text-[var(--ct-btn-primary-text)] shadow-[var(--ct-shadow-gold)]"
                  />
                </div>
                {user && user.role === "customer" ? (
                  <Link to="/cont" onClick={() => setOpen(false)} data-testid="mobile-account-cta" className="inline-flex items-center justify-center gap-2 rounded-[3px] text-sm font-medium" style={{ height: "48px", background: "var(--ct-surface)", color: "var(--ct-text)", border: "1px solid var(--ct-border-gold)" }}>
                    <UserCircle size={16} style={{ color: "var(--ct-gold)" }} /> Contul meu
                  </Link>
                ) : user && user.role === "admin" ? (
                  <Link to="/admin" onClick={() => setOpen(false)} data-testid="mobile-admin-cta" className="inline-flex items-center justify-center gap-2 rounded-[3px] text-sm font-medium" style={{ height: "48px", background: "var(--ct-surface)", color: "var(--ct-text)", border: "1px solid var(--ct-border-gold)" }}>
                    <UserCircle size={16} style={{ color: "var(--ct-gold)" }} /> Admin
                  </Link>
                ) : (
                  <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-login-cta" className="inline-flex items-center justify-center gap-2 rounded-[3px] text-sm font-medium" style={{ height: "48px", background: "var(--ct-surface)", color: "var(--ct-text)", border: "1px solid var(--ct-border-gold)" }}>
                    <LogIn size={16} style={{ color: "var(--ct-gold)" }} /> Intră în cont
                  </Link>
                )}
              </div>
            </nav>
          </div>

          {/* Close X — fixed in top-right, NOT inside the panel (matches the mockup spec) */}
          <button
            ref={closeBtnRef}
            type="button"
            onClick={() => setOpen(false)}
            className="lg:hidden xl:hidden fixed top-[22px] right-[22px] z-50 p-2"
            aria-label="Închide meniul"
            data-testid="mobile-menu-close"
            style={{ color: "#F7F5F0", fontSize: "32px", lineHeight: 1 }}
          >
            <X size={32} />
          </button>
        </>
      )}
    </header>
  );
}
